import React from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type FeedbackContext = 'demographics' | 'annotation' | 'completion';

interface FeedbackButtonProps {
  className?: string;
  position?: 'fixed' | 'relative';
  context: FeedbackContext;
  sessionId?: string;
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * Context detection utility to determine current annotation context
 */
export function detectFeedbackContext(): FeedbackContext {
  const path = window.location.pathname;
  
  if (path.includes('/demographics')) {
    return 'demographics';
  } else if (path.includes('/annotation')) {
    return 'annotation';
  } else if (path.includes('/complete')) {
    return 'completion';
  }
  
  // Default fallback based on current route
  return 'annotation';
}

/**
 * Standalone feedback button component
 * Implements requirements 1.1, 7.1, 7.2, 7.4, 7.5
 */
export function FeedbackButton({ 
  className, 
  position = 'fixed', 
  context, 
  sessionId,
  onClick,
  disabled = false
}: FeedbackButtonProps) {

  const buttonClasses = cn(
    // Base button styling
    "gap-2 shadow-lg hover:shadow-xl transition-all duration-200",
    // Position-specific styling
    position === 'fixed' && [
      "fixed z-50",
      "bottom-4 right-4", // Default fixed positioning
      "md:bottom-6 md:right-6" // Responsive positioning
    ],
    className
  );

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
      size="default"
      aria-label="Provide feedback about your annotation experience"
      title="Share feedback"
      // Accessibility attributes
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      data-feedback-context={context}
      data-session-id={sessionId}
    >
      <MessageCircle className="h-4 w-4" />
      <span className="hidden sm:inline">Feedback</span>
    </Button>
  );
}

/**
 * Auto-detecting feedback button that determines context from current route
 */
export function AutoFeedbackButton(props: Omit<FeedbackButtonProps, 'context'>) {
  const context = detectFeedbackContext();
  
  return <FeedbackButton {...props} context={context} />;
}